// ─── Écran RECETTES : bibliothèque de recettes ────────────────────
// Groupées par catégorie (repasConfig.categoriesRecette, repli sur le
// catalogue par défaut). Le coût affiché est indicatif (somme des prix
// d'ingrédients renseignés) — sert de base au coût d'un Repas.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, SectionPliable, HIT,
  useUI, useT, useAuth, useEspace, confirmerSuppression, confirmerChoix,
} from '@mop/core';
import { ecouterRecettes, creerRecette, modifierRecette, supprimerRecette, coutRecette } from './services/recettes';
import { ecouterRepasConfig, categoriesRecetteDe, ordreCategorieRecette, categorieRecetteDe } from './services/menus';
import { exporterFichier, choisirEtLireFichier } from './services/io';
import { recettesVersMoPCal, parseImportRecettes } from './services/recettesIO';
import { ecouterBase } from './services/ingredientsBase';
import { ecouterPerso, ajouterPersonnel, ingredientsEffectifs } from './services/ingredientsPerso';
import { ingredientsRecetteEchelle } from './services/courses';
import { personnesRecette } from './services/personnes';
import useCatalogueIngredients from './hooks/useCatalogueIngredients';
import { ecouterListeCourses, enregistrerListeCourses, fusionnerDansListe } from './services/listeCourses';
import FeuilleRecette from './FeuilleRecette';
import FicheRecette from './FicheRecette';
import FeuilleAjoutCourses from './FeuilleAjoutCourses';

export default function EcranRecettes() {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const { tenantId, assurerEspace } = useEspace();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [recettes, setRecettes] = useState([]);
  const [config, setConfig] = useState(null);
  const [base, setBase] = useState([]);
  const [perso, setPerso] = useState({ ajouts: [], masques: [] });
  const [feuille, setFeuille] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [listeCourses, setListeCourses] = useState([]);
  const [ajout, setAjout] = useState(null);   // recette dont on remplit les courses
  const [erreur, setErreur] = useState('');
  const [fermes, setFermes] = useState(() => new Set());
  const basculerCategorie = (id) => setFermes(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const signaler = (quoi) => (e) => setErreur(`${quoi} — ${e?.message || e?.code || 'erreur'}`);

  useEffect(() => ecouterRecettes(tenantId, (liste) => { setErreur(''); setRecettes(liste); }, signaler('recettes')), [tenantId]);
  useEffect(() => ecouterRepasConfig(tenantId, setConfig), [tenantId]);
  useEffect(() => ecouterBase(setBase), []);
  useEffect(() => ecouterPerso(user?.uid, setPerso), [user?.uid]);
  useEffect(() => ecouterListeCourses(tenantId, setListeCourses), [tenantId]);
  const catalogue = useMemo(() => ingredientsEffectifs(base, perso), [base, perso]);
  const { emojiPour: emojiIngredient } = useCatalogueIngredients();

  // La feuille d'ajout renvoie les ingrédients DÉJÀ mis à l'échelle, et
  // éventuellement corrigés à la main : on ne recalcule rien ici.
  const ajouterIngredients = async (ingredients) => {
    try {
      const espaceId = tenantId || await assurerEspace();
      await enregistrerListeCourses(espaceId, fusionnerDansListe(listeCourses, ingredients));
      Alert.alert(t('courses.ajoutTitre'), t('courses.ajoutMsg', { n: ingredients.length }));
    } catch (e) { echec(e); }
  };

  // Alimente MES ajouts perso avec les ingrédients de la recette qui ne sont
  // pas déjà dans le référentiel (base OU perso), best-effort — on n'écrit
  // jamais dans la base globale depuis ce flux.
  const alimenterCatalogue = async (ingredients) => {
    try {
      let persoActuel = perso;   // copie locale : plusieurs ajouts d'affilée ne doivent pas s'écraser
      for (const ing of ingredients) {
        const nom = (ing.nom || '').trim();
        if (!nom) continue;
        if (catalogue.some(c => c.nom.toLowerCase() === nom.toLowerCase())) continue;
        await ajouterPersonnel(user.uid, persoActuel, { nom, unite: ing.unite });
        persoActuel = { ...persoActuel, ajouts: [...persoActuel.ajouts, { nom, unite: ing.unite || '' }] };
      }
    } catch { /* best-effort */ }
  };

  const cats = categoriesRecetteDe(config);
  const echec = (e) => Alert.alert(t('repas.erreur'), e?.message || '');

  const exporter = async () => {
    try {
      await exporterFichier({
        nomFichier: `recettes-${new Date().toISOString().slice(0, 10)}.json`,
        contenu: recettesVersMoPCal(recettes),
      });
    } catch (e) { echec(e); }
  };

  const importer = async () => {
    try {
      const texte = await choisirEtLireFichier();
      if (texte == null) return;
      const { recettes: importees, erreur: err } = parseImportRecettes(texte);
      if (err) return Alert.alert(t('repas.erreur'), err);
      if (!importees.length) return Alert.alert(t('repas.erreur'), t('repas.importAucune'));
      confirmerChoix(t('repas.importTitre'), t('repas.importConfirm', { n: importees.length }), [
        { text: t('commun.annuler'), style: 'cancel' },
        {
          text: t('repas.importer'), onPress: async () => {
            try {
              const espaceId = tenantId || await assurerEspace();
              for (const r of importees) {
                await creerRecette({ ...r, espaceId, creePar: user?.uid, creeParNom: user?.nom });
              }
              Alert.alert(t('repas.importTitre'), t('repas.importOk', { n: importees.length }));
            } catch (e) { echec(e); }
          },
        },
      ]);
    } catch (e) { echec(e); }
  };

  const enregistrer = async (r) => {
    try {
      const espaceId = tenantId || await assurerEspace();
      if (feuille?.initial?.id) await modifierRecette(feuille.initial.id, r);
      else await creerRecette({ ...r, espaceId, creePar: user?.uid, creeParNom: user?.nom });
      setFeuille(null);
      alimenterCatalogue(r.ingredients || []);
    } catch (e) { echec(e); }
  };

  const groupes = useMemo(() => {
    const map = {};
    for (const r of recettes) { const id = r.categorie || ''; (map[id] = map[id] || []).push(r); }
    return Object.keys(map)
      .sort((a, b) => ordreCategorieRecette(a, cats) - ordreCategorieRecette(b, cats))
      .map(id => ({ cat: categorieRecetteDe(id, cats), items: map[id] }));
  }, [recettes, cats]);

  return (
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      <PageHeader titre={t('repas.recettes')} iconeTitre="🍽️"
        action={{ icone: '＋', label: t('repas.nouvelle'), onPress: () => setFeuille({ initial: null }) }}>
        <TouchableOpacity onPress={importer} hitSlop={HIT} accessibilityLabel={t('repas.importer')}>
          <Text style={s.headerIcone}>📥</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={exporter} hitSlop={HIT} accessibilityLabel={t('repas.exporter')}>
          <Text style={s.headerIcone}>📤</Text>
        </TouchableOpacity>
      </PageHeader>

      <ScrollView contentContainerStyle={s.corps}>
        {!!erreur && (
          <TouchableOpacity style={s.erreur} activeOpacity={0.8} onPress={() => setErreur('')}>
            <Text style={s.erreurTxt}>⚠️ {erreur}</Text>
          </TouchableOpacity>
        )}

        {recettes.length === 0 ? (
          <EmptyState icon="🍽️" texte={t('repas.aucune')} aide={t('repas.aucuneAide')} />
        ) : groupes.map(g => (
          <SectionPliable key={g.cat.id || '_'} titre={g.cat.label} emoji={g.cat.emoji}
            ouvert={!fermes.has(g.cat.id || '_')} onToggle={() => basculerCategorie(g.cat.id || '_')}>
            {g.items.map(r => (
              <LigneSwipe key={r.id} espacement={8} enabled
                onEditer={() => setFeuille({ initial: r })}
                onSupprimer={() => confirmerSuppression(t('repas.supprTitre'), r.nom || t('repas.sansNom'),
                  () => supprimerRecette(r.id).catch(echec))}>
                <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => setFiche(r)}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={s.nom} numberOfLines={1}>{r.nom || t('repas.sansNom')}</Text>
                    <Text style={s.sous} numberOfLines={1}>
                      {t('repas.nIngredients', { n: (r.ingredients || []).length })}
                      {r.tempsMin ? `  ·  ${t('repas.nMin', { n: r.tempsMin })}` : ''}
                    </Text>
                  </View>
                  {coutRecette(r) > 0 && <Text style={s.cout}>{coutRecette(r).toFixed(2)} €</Text>}
                </TouchableOpacity>
              </LigneSwipe>
            ))}
          </SectionPliable>
        ))}
      </ScrollView>

      <FicheRecette visible={!!fiche} recette={fiche} categorie={fiche ? categorieRecetteDe(fiche.categorie, cats) : null}
        onFermer={() => setFiche(null)}
        onEditer={() => { setFeuille({ initial: fiche }); setFiche(null); }}
        onSupprimer={() => confirmerSuppression(t('repas.supprTitre'), fiche?.nom || t('repas.sansNom'),
          () => { supprimerRecette(fiche.id).catch(echec); setFiche(null); })}
        onAjouterListe={() => setAjout(fiche)} />

      {/* Ajout aux courses : modale SŒUR de la fiche (jamais imbriquée). */}
      <FeuilleAjoutCourses visible={!!ajout} titre={ajout?.nom} emoji="🍽️"
        mode="personnes" personnesInit={ajout ? personnesRecette(ajout) : 4}
        calculer={(n) => (ajout ? ingredientsRecetteEchelle(ajout, n) : [])}
        emojiPour={emojiIngredient}
        onValider={ajouterIngredients} onFermer={() => setAjout(null)} />

      <FeuilleRecette visible={!!feuille} initial={feuille?.initial} categories={cats} catalogue={catalogue}
        onEnregistrer={enregistrer} onFermer={() => setFeuille(null)} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  headerIcone: { fontSize: 19, color: UI.accent },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13 },
  nom: { fontSize: 15, fontWeight: '800', color: UI.ink },
  sous: { fontSize: 12, color: UI.muted, marginTop: 2 },
  cout: { fontSize: 14, fontWeight: '800', color: UI.ink },
  erreur: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.danger || '#c0392b', padding: 11, marginBottom: 10 },
  erreurTxt: { fontSize: 13, color: UI.danger || '#c0392b', fontWeight: '700' },
});
